/**
 * The org the console is acting in, as the browser knows it (JSS#1182).
 *
 * The BFF decides the acting org (`X-Org-Override`, `requireOrgSelection`):
 * the org switcher's pick when there is one, else the user's only org. This
 * mirrors that so a page can name the org in a link. A superuser in the
 * all-orgs view acts in no org (undefined).
 */
import { useQuery } from '@tanstack/react-query';
import { getOrgOverride, meApi } from '@/api/client';
import { useAuth } from '@/contexts/AuthContext';
import type { MyOrganization } from '@/types';

export function actingOrgIdOf(
  superuser: boolean,
  override: string | null,
  orgs: readonly Pick<MyOrganization, 'id'>[] | undefined,
  tokenOrgId: string | undefined,
): string | undefined {
  if (override) return override;
  if (superuser) return undefined;
  if (orgs?.length === 1) return orgs[0].id;
  return tokenOrgId || undefined;
}

/** The switcher's list (Header.tsx, same query key) and the acting org within it. */
export function useActingOrg(enabled = true) {
  const { user, isSuperUser } = useAuth();
  const superuser = isSuperUser();
  // A lambda, so a test that mocks only part of the API client fails the
  // query (tolerated) instead of the render.
  const organizations = useQuery({
    queryKey: ['my-organizations'],
    queryFn: () => meApi.organizations(),
    enabled,
    staleTime: 60_000,
  });
  return {
    superuser,
    organizations,
    actingOrgId: actingOrgIdOf(superuser, getOrgOverride(), organizations.data, user?.orgId),
  };
}
